import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import type { DateTime } from 'luxon';
import { useState } from 'react';
import { Alert, Button, Col, Dropdown, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { RecurrenceTaskStatus, User } from '../../service/api';
import { RecurrenceTaskStatusEnum } from '../../service/api';
import useManagerTasksStore from '../../store/managerTasks';
import UserAvatar from '../UserAvatar';

interface UserTaskStatus {
  user: User;
  taskId: string;
  status: RecurrenceTaskStatus;
}

interface MultiUserTaskStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskTitle: string;
  taskIcon: IconName;
  start: DateTime;
  stop: DateTime;
  recurrenceId: string;
  userTasks: UserTaskStatus[];
}

export default function MultiUserTaskStatusModal({
  isOpen,
  onClose,
  taskTitle,
  taskIcon,
  start,
  stop,
  recurrenceId,
  userTasks,
}: MultiUserTaskStatusModalProps) {
  const { t } = useTranslation();
  const { updateTaskStatus } = useManagerTasksStore();
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize user statuses from provided user tasks
  const [userStatuses, setUserStatuses] = useState<UserTaskStatus[]>(userTasks);

  const handleStatusChange = async (
    userId: string,
    newStatus: RecurrenceTaskStatus,
  ) => {
    if (isProcessing) return;

    const userTask = userStatuses.find(ut => ut.user._id === userId);
    if (!userTask) return;

    setIsProcessing(userId);
    setError(null);

    try {
      await updateTaskStatus(
        userTask.taskId,
        newStatus,
        recurrenceId,
        start,
        stop,
      );

      // Update local state
      setUserStatuses(prev =>
        prev.map(userStatus =>
          userStatus.user._id === userId
            ? { ...userStatus, status: newStatus }
            : userStatus,
        ),
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update task status',
      );
    } finally {
      setIsProcessing(null);
    }
  };

  const formatTime = (dateTime: DateTime) => {
    return dateTime.toLocal().toFormat('h:mm a');
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3">
          <DynamicIcon name={taskIcon} size={24} className="text-primary" />
          {taskTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <p className="text-muted small mb-0">
            {t('manager.tasks.scheduled')}: {formatTime(start)} -{' '}
            {formatTime(stop)}
          </p>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <h6 className="mb-3">{t('manager.tasks.assignedUsers')}</h6>

        {userStatuses.map(({ user, status }) => (
          <Row
            key={user._id}
            className="align-items-center mb-3 p-3 border rounded"
          >
            <Col xs={8}>
              <div className="d-flex align-items-center gap-2">
                <UserAvatar user={user} size={32} />
                <span className="fw-medium">
                  {user.given_name} {user.family_name}
                </span>
              </div>
            </Col>
            <Col xs={4} className="text-end">
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="light"
                  size="sm"
                  disabled={isProcessing === user._id}
                  className="border px-3"
                >
                  {isProcessing === user._id ? (
                    <div
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                    />
                  ) : null}
                  {t(`manager.tasks.status.${status}`)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {Object.values(RecurrenceTaskStatusEnum).map(statusOption => (
                    <Dropdown.Item
                      key={statusOption}
                      onClick={() => handleStatusChange(user._id, statusOption)}
                      active={status === statusOption}
                      disabled={
                        status === statusOption || isProcessing === user._id
                      }
                      className="pt-3 pb-3"
                    >
                      {t(`manager.tasks.status.${statusOption}`)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
